import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Check, Inbox } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/contacts").then(({ data }) => setMessages(data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/admin/contacts/${id}/read`);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div className="p-6 sm:p-10" data-testid="admin-messages">
      <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Customer Support</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">
        Messages {unread > 0 && <span className="ml-2 align-middle rounded-full bg-[#2F80ED] px-3 py-1 font-mono text-sm text-white">{unread} new</span>}
      </h1>

      {loading ? (
        <div className="mt-8 h-40 animate-pulse bg-black/5" />
      ) : messages.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-4 text-center text-black/40" data-testid="no-messages">
          <Inbox className="h-12 w-12" />
          <p className="font-display text-lg">No messages yet</p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`border p-6 ${m.read ? "border-black/10 bg-white" : "border-[#2F80ED]/40 bg-[#2F80ED]/5"}`}
              data-testid={`message-${m.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-semibold">{m.name}</span>
                    {!m.read && <Badge className="rounded-full bg-[#2F80ED] px-2 py-0.5 font-mono text-[9px] uppercase text-white">New</Badge>}
                  </div>
                  <a href={`mailto:${m.email}`} className="font-mono text-xs text-[#2F80ED] hover:underline">{m.email}</a>
                  {m.phone && <span className="ml-3 font-mono text-xs text-black/50">{m.phone}</span>}
                </div>
                <div className="text-right">
                  <div className="font-mono text-[11px] uppercase tracking-overline text-black/40">
                    {new Date(m.created_at).toLocaleString("en-IN")}
                  </div>
                  {!m.read && (
                    <button
                      onClick={() => markRead(m.id)}
                      className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-overline text-[#2F80ED] hover:underline"
                      data-testid={`mark-read-${m.id}`}
                    >
                      <Check className="h-3 w-3" /> Mark read
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-overline text-black/40">
                Subject: <span className="text-black/70">{m.subject}</span>
              </div>
              <p className="mt-2 leading-relaxed text-black/70">{m.message}</p>
              <a
                href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                className="mt-4 inline-flex items-center gap-2 border border-black/15 px-4 py-2 font-mono text-xs uppercase tracking-overline hover:bg-black hover:text-white"
                data-testid={`reply-${m.id}`}
              >
                <Mail className="h-3.5 w-3.5" /> Reply by email
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
